/**
 * Catálogo centralizado das 10 Categorias Temáticas e Metadados Geométricos do SAPL Map.
 *
 * Sincronizado com os classificadores e resolvers espaciais do nlp-geocoder-br.
 * Mantém retrocompatibilidade com taxonomias legadas (Infraestrutura, Meio Ambiente, etc.).
 *
 * @module constants/categories
 */

/**
 * Definição oficial das 15 Categorias Temáticas (14 municipais + OUTROS) com cores, rótulos e ícones SVG.
 */
export const THEMATIC_CATEGORIES = {
  INFRAESTRUTURA_VIARIA: {
    key: "INFRAESTRUTURA_VIARIA",
    label: "Infraestrutura e Vias",
    solid: "#1D4ED8", // Cobalto Profundo
    badgeBg: "#DBEAFE",
    badgeFg: "#1E40AF",
    onSolid: "#FFFFFF",
    bgColor: "#1D4ED8", // Retrocompatibilidade
    iconColor: "#FFFFFF", // Retrocompatibilidade
    // Estrada asfaltada com faixa central
    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-road"><path d="M12 17v4"/><path d="M12 5V3"/><path d="M12 9v3"/><path d="M2.077 18.449A2 2 0 0 0 4 21h16a2 2 0 0 0 1.924-2.55l-4-14A2 2 0 0 0 16 3H8a2 2 0 0 0-1.924 1.45z"/></svg>`,
  },
  TRANSITO_SINALIZACAO: {
    key: "TRANSITO_SINALIZACAO",
    label: "Trânsito e Sinalização",
    solid: "#F97316", // Laranja 500
    badgeBg: "#FFEDD5",
    badgeFg: "#C2410C",
    onSolid: "#0F172A",
    bgColor: "#F97316", // Retrocompatibilidade
    iconColor: "#0F172A", // Retrocompatibilidade
    // Cone de trânsito / sinalização de alerta
    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-traffic-cone"><path d="M16.05 10.966a5 2.5 0 0 1-8.1 0"/><path d="m16.923 14.049 4.48 2.04a1 1 0 0 1 .001 1.831l-8.574 3.9a2 2 0 0 1-1.66 0l-8.574-3.91a1 1 0 0 1 0-1.83l4.484-2.04"/><path d="M16.949 14.14a5 2.5 0 1 1-9.9 0L10.063 3.5a2 2 0 0 1 3.874 0z"/><path d="M9.194 6.57a5 2.5 0 0 0 5.61 0"/></svg>`,
  },
  TRANSPORTE_PUBLICO: {
    key: "TRANSPORTE_PUBLICO",
    label: "Transporte Público",
    solid: "#0284C7", // Sky 600
    badgeBg: "#E0F2FE",
    badgeFg: "#0369A1",
    onSolid: "#FFFFFF",
    bgColor: "#0284C7", // Retrocompatibilidade
    iconColor: "#FFFFFF", // Retrocompatibilidade
    // Ônibus urbano
    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-bus"><path d="M8 6v6"/><path d="M15 6v6"/><path d="M2 12h19.6"/><path d="M18 18h3s.5-1.7.8-2.8c.1-.4.2-.8.2-1.2 0-.4-.1-.8-.2-1.2l-1.4-5C20.1 6.8 19.1 6 18 6H4C2.9 6 1.9 6.8 1.6 7.8L.2 12.8c-.1.4-.2.8-.2 1.2 0 .4.1.8.2 1.2.3 1.1.8 2.8.8 2.8h3"/><circle cx="7" cy="18" r="2"/><path d="M9 18h5"/><circle cx="16" cy="18" r="2"/></svg>`,
  },
  ILUMINACAO_PUBLICA: {
    key: "ILUMINACAO_PUBLICA",
    label: "Iluminação Pública",
    solid: "#EAB308", // Âmbar 500
    badgeBg: "#FEF9C3",
    badgeFg: "#A16207",
    onSolid: "#0F172A",
    bgColor: "#EAB308", // Retrocompatibilidade
    iconColor: "#0F172A", // Retrocompatibilidade
    // Lâmpada acesa / poste de luz
    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-lightbulb"><path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"/><path d="M9 18h6"/><path d="M10 22h4"/></svg>`,
  },
  LIMPEZA_ZELADORIA: {
    key: "LIMPEZA_ZELADORIA",
    label: "Limpeza e Zeladoria",
    solid: "#10B981", // Esmeralda 500
    badgeBg: "#D1FAE5",
    badgeFg: "#047857",
    onSolid: "#0F172A",
    bgColor: "#10B981", // Retrocompatibilidade
    iconColor: "#0F172A", // Retrocompatibilidade
    // Vassoura de zeladoria / folha ambiental
    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-brush-cleaning"><path d="m16 22-1-4"/><path d="M19 14a1 1 0 0 0 1-1v-1a2 2 0 0 0-2-2h-3a1 1 0 0 1-1-1V4a2 2 0 0 0-4 0v5a1 1 0 0 1-1 1H6a2 2 0 0 0-2 2v1a1 1 0 0 0 1 1"/><path d="M19 14H5l-1.973 6.767A1 1 0 0 0 4 22h16a1 1 0 0 0 .973-1.233z"/><path d="m8 22 1-4"/></svg>`,
  },
  MEIO_AMBIENTE: {
    key: "MEIO_AMBIENTE",
    label: "Meio Ambiente",
    solid: "#16A34A", // Verde Folhagem Botânico 600
    badgeBg: "#DCFCE7",
    badgeFg: "#15803D",
    onSolid: "#FFFFFF",
    bgColor: "#16A34A", // Retrocompatibilidade
    iconColor: "#FFFFFF", // Retrocompatibilidade
    // Árvore / preservação ambiental
    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-tree-deciduous"><path d="M8 19a4 4 0 0 1-2.24-7.32A3.5 3.5 0 0 1 9 6.03V6a3 3 0 1 1 6 0v.04a3.5 3.5 0 0 1 3.24 5.65A4 4 0 0 1 16 19Z"/><path d="M12 19v3"/></svg>`,
  },
  SANEAMENTO_AGUA: {
    key: "SANEAMENTO_AGUA",
    label: "Saneamento e Água",
    solid: "#06B6D4", // Ciano 500
    badgeBg: "#CFFAFE",
    badgeFg: "#0E7490",
    onSolid: "#0F172A",
    bgColor: "#06B6D4", // Retrocompatibilidade
    iconColor: "#0F172A", // Retrocompatibilidade
    // Gota d'água fluida
    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-droplets"><path d="M7 16.3c2.2 0 4-1.83 4-4.05 0-1.16-.57-2.26-1.71-3.19S7.29 6.75 7 5.3c-.29 1.45-1.14 2.84-2.29 3.76S3 11.1 3 12.25c0 2.22 1.8 4.05 4 4.05z"/><path d="M12.56 6.6A10.97 10.97 0 0 0 14 3.02c.5 2.5 2 4.9 4 6.5s3 3.5 3 5.5a6.98 6.98 0 0 1-11.91 4.97"/></svg>`,
  },
  SAUDE: {
    key: "SAUDE",
    label: "Saúde",
    solid: "#DC2626", // Vermelho Clínico 600
    badgeBg: "#FEE2E2",
    badgeFg: "#B91C1C",
    onSolid: "#FFFFFF",
    bgColor: "#DC2626", // Retrocompatibilidade
    iconColor: "#FFFFFF", // Retrocompatibilidade
    // Cruz médica sólida
    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-heart"><path d="M2 9.5a5.5 5.5 0 0 1 9.591-3.676.56.56 0 0 0 .818 0A5.49 5.49 0 0 1 22 9.5c0 2.29-1.5 4-3 5.5l-5.492 5.313a2 2 0 0 1-3 .019L5 15c-1.5-1.5-3-3.2-3-5.5"/></svg>`,
  },
  EDUCACAO: {
    key: "EDUCACAO",
    label: "Educação",
    solid: "#8B5CF6", // Roxo 500
    badgeBg: "#EDE9FE",
    badgeFg: "#6D28D9",
    onSolid: "#FFFFFF",
    bgColor: "#8B5CF6", // Retrocompatibilidade
    iconColor: "#FFFFFF", // Retrocompatibilidade
    // Capelo de Formatura
    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-graduation-cap"><path d="M21.42 10.922a1 1 0 0 0-.019-1.838L12.83 5.18a2 2 0 0 0-1.66 0L2.6 9.08a1 1 0 0 0 0 1.832l8.57 3.908a2 2 0 0 0 1.66 0z"/><path d="M22 10v6"/><path d="M6 12.5V16a6 3 0 0 0 12 0v-3.5"/></svg>`,
  },
  ESPORTE_LAZER: {
    key: "ESPORTE_LAZER",
    label: "Esporte e Lazer",
    solid: "#84CC16", // Lima 500
    badgeBg: "#ECFCCB",
    badgeFg: "#4D7C0F",
    onSolid: "#0F172A",
    bgColor: "#84CC16", // Retrocompatibilidade
    iconColor: "#0F172A", // Retrocompatibilidade
    // Bola esportiva e parque
    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-volleyball"><path d="M11 7a16 16 20 0 1 10.98 4.362"/><path d="M12 12a13 13 0 0 1-8.66 5"/><path d="M16.83 13.634a16 16 0 0 1-9.267 7.328"/><path d="M20.66 17A13 13 0 0 0 12 12a13 13 0 0 1 0-10"/><path d="M8.17 15.366a16 16 0 0 1-1.713-11.69"/><circle cx="12" cy="12" r="10"/></svg>`,
  },
  SEGURANCA: {
    key: "SEGURANCA",
    label: "Segurança",
    solid: "#6366F1", // Índigo 500
    badgeBg: "#E0E7FF",
    badgeFg: "#4338CA",
    onSolid: "#FFFFFF",
    bgColor: "#6366F1", // Retrocompatibilidade
    iconColor: "#FFFFFF", // Retrocompatibilidade
    // Escudo Protetor
    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-shield"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/></svg>`,
  },
  CULTURA_EVENTOS: {
    key: "CULTURA_EVENTOS",
    label: "Cultura e Eventos",
    solid: "#D946EF", // Fúcsia 500
    badgeBg: "#FAE8FF",
    badgeFg: "#A21CAF",
    onSolid: "#FFFFFF",
    bgColor: "#D946EF", // Retrocompatibilidade
    iconColor: "#FFFFFF", // Retrocompatibilidade
    // Paleta de artes e eventos culturais
    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-palette"><circle cx="13.5" cy="6.5" r=".5" fill="currentColor"/><circle cx="17.5" cy="10.5" r=".5" fill="currentColor"/><circle cx="8.5" cy="7.5" r=".5" fill="currentColor"/><circle cx="6.5" cy="12.5" r=".5" fill="currentColor"/><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z"/></svg>`,
  },
  ASSISTENCIA_SOCIAL_HABITACAO: {
    key: "ASSISTENCIA_SOCIAL_HABITACAO",
    label: "Assistência Social e Habitação",
    solid: "#DB2777", // Rosa Framboesa Acolhedor 600
    badgeBg: "#FCE7F3",
    badgeFg: "#9D174D",
    onSolid: "#FFFFFF",
    bgColor: "#DB2777", // Retrocompatibilidade
    iconColor: "#FFFFFF", // Retrocompatibilidade
    // Mão acolhendo coração / assistência comunitária
    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-hand-heart"><path d="M11 14h2a2 2 0 0 0 0-4h-3c-.6 0-1.1.2-1.4.6L3 16"/><path d="m14.45 13.39 5.05-4.694C20.196 8 21 6.85 21 5.75a2.75 2.75 0 0 0-4.797-1.837.276.276 0 0 1-.406 0A2.75 2.75 0 0 0 11 5.75c0 1.2.802 2.248 1.5 2.946L16 11.95"/><path d="m2 15 6 6"/><path d="m7 20 1.6-1.4c.3-.4.8-.6 1.4-.6h4c1.1 0 2.1-.4 2.8-1.2l4.6-4.4a1 1 0 0 0-2.75-2.91"/></svg>`,
  },
  ADMINISTRACAO: {
    key: "ADMINISTRACAO",
    label: "Administração Pública",
    solid: "#334155", // Ardósia Nobre / Chumbo 700
    badgeBg: "#F1F5F9",
    badgeFg: "#1E293B",
    onSolid: "#FFFFFF",
    bgColor: "#334155", // Retrocompatibilidade
    iconColor: "#FFFFFF", // Retrocompatibilidade
    // Prédio administrativo / paço municipal
    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-building-2"><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"/><path d="M10 6h4"/><path d="M10 10h4"/><path d="M10 14h4"/><path d="M10 18h4"/></svg>`,
  },
  OUTROS: {
    key: "OUTROS",
    label: "Outros",
    solid: "#64748B", // Ardósia 500
    badgeBg: "#F8FAFC",
    badgeFg: "#475569",
    onSolid: "#FFFFFF",
    bgColor: "#64748B", // Retrocompatibilidade
    iconColor: "#FFFFFF", // Retrocompatibilidade
    // Ponto de interrogação / pasta
    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-file"><path d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z"/><path d="M14 2v5a1 1 0 0 0 1 1h5"/></svg>`,
  },
};

/**
 * Ordem padrão para exibição em listas, filtros e painéis de estatísticas.
 */
export const THEMATIC_CATEGORIES_ORDER = [
  "INFRAESTRUTURA_VIARIA",
  "TRANSITO_SINALIZACAO",
  "TRANSPORTE_PUBLICO",
  "ILUMINACAO_PUBLICA",
  "SANEAMENTO_AGUA",
  "LIMPEZA_ZELADORIA",
  "MEIO_AMBIENTE",
  "SAUDE",
  "EDUCACAO",
  "ESPORTE_LAZER",
  "SEGURANCA",
  "CULTURA_EVENTOS",
  "ASSISTENCIA_SOCIAL_HABITACAO",
  "ADMINISTRACAO",
  "OUTROS",
];

/**
 * Metadados dos tipos de geometria GeoJSON retornados pelo nlp-geocoder-br.
 */
export const GEOMETRY_TYPE_METADATA = {
  LineString: {
    label: "Trecho de via",
    badge: "Trecho de rua",
    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-route"><circle cx="6" cy="19" r="3"/><path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15"/><circle cx="18" cy="5" r="3"/></svg>`,
    description: "Segmento de logradouro georreferenciado",
  },
  MultiLineString: {
    label: "Trecho de via",
    badge: "Trecho de rua",
    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-split"><path d="M16 3h5v5"/><path d="M8 3H3v5"/><path d="M12 22v-8.3a4 4 0 0 0-1.172-2.872L3 3"/><path d="m15 9 6-6"/></svg>`,
    description: "Segmento de logradouro com múltiplos traçados",
  },
  Polygon: {
    label: "Bairro / Área",
    badge: "Área / Bairro",
    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-vector-polygon"><path d="m12.828 4.813 5.344 2.375"/><path d="m15.769 18.153 3.461-8.306"/><path d="m5.687 14.074 7.625 4.852"/><path d="M9.772 5.579 5.228 11.42"/><circle cx="11" cy="4" r="2"/><circle cx="15" cy="20" r="2"/><circle cx="20" cy="8" r="2"/><circle cx="4" cy="13" r="2"/></svg>`,
    description: "Perímetro poligonal da região ou bairro",
  },
  MultiPoint: {
    label: "Cruzamento / Esquina",
    badge: "Cruzamento",
    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-move"><path d="M12 2v20"/><path d="m15 19-3 3-3-3"/><path d="m19 9 3 3-3 3"/><path d="M2 12h20"/><path d="m5 9-3 3 3 3"/><path d="m9 5 3-3 3 3"/></svg>`,
    description: "Pontos de intersecção entre vias",
  },
  Point: {
    label: "Endereço pontual",
    badge: "Endereço",
    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-map-pin"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"/><circle cx="12" cy="10" r="3"/></svg>`,
    description: "Ponto geocodificado de endereço",
  },
  GeometryCollection: {
    label: "Geometria composta",
    badge: "Múltiplas feições",
    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-send-to-back"><rect x="14" y="14" width="8" height="8" rx="2"/><rect x="2" y="2" width="8" height="8" rx="2"/><path d="M7 14v1a2 2 0 0 0 2 2h1"/><path d="M14 7h1a2 2 0 0 1 2 2v1"/></svg>`,
    description: "Conjunto com múltiplas feições espaciais",
  },
};

/**
 * Rótulos amigáveis para os tipos de relações espaciais identificadas pelo solver.
 */
export const RELATION_TYPE_LABELS = {
  BETWEEN_SEGMENT: "Entre vias",
  INTERSECTION: "Cruzamento de vias",
  ALONG_ROAD: "Ao longo da via",
  NEAR_LANDMARK: "Próximo a ponto de referência",
  AT_NUMBER: "Número predial",
  NEIGHBORHOOD: "Bairro / Perímetro",
};

/**
 * Obtém o estilo visual completo (cor, ícone, label) a partir de qualquer identificador
 * de categoria (chave do banco, nome formatado ou categoria legada).
 *
 * @param {string|object} categoryKeyOrName
 * @returns {{ key: string, label: string, solid: string, badgeBg: string, badgeFg: string, onSolid: string, bgColor: string, iconColor: string, icon: string }}
 */
export function getCategoryStyle(categoryKeyOrName) {
  if (!categoryKeyOrName) return THEMATIC_CATEGORIES.OUTROS;

  const raw = typeof categoryKeyOrName === "object"
    ? categoryKeyOrName.thematic_category || categoryKeyOrName.nome || categoryKeyOrName.name || ""
    : String(categoryKeyOrName);

  const clean = raw.trim().toUpperCase();

  // 1. Match direto por chave oficial
  if (THEMATIC_CATEGORIES[clean]) {
    return THEMATIC_CATEGORIES[clean];
  }

  // Alias para ASSISTENCIA_SOCIAL
  if (clean === "ASSISTENCIA_SOCIAL") {
    return THEMATIC_CATEGORIES.ASSISTENCIA_SOCIAL_HABITACAO;
  }

  // 2. Normalização fonética / sem acentos
  const norm = raw
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  if (norm.includes("paviment") || norm.includes("via") || norm.includes("asfalt") || norm.includes("infra") || norm.includes("buraco") || norm.includes("ponte")) {
    return THEMATIC_CATEGORIES.INFRAESTRUTURA_VIARIA;
  }
  if (norm.includes("transit") || norm.includes("sinaliz") || norm.includes("placa") || norm.includes("semafor") || norm.includes("lombada")) {
    return THEMATIC_CATEGORIES.TRANSITO_SINALIZACAO;
  }
  if (norm.includes("transporte") || norm.includes("onibus") || norm.includes("itinerario") || norm.includes("terminal") || norm.includes("abrigo")) {
    return THEMATIC_CATEGORIES.TRANSPORTE_PUBLICO;
  }
  if (norm.includes("ilumin") || norm.includes("lampad") || norm.includes("poste") || norm.includes("braco")) {
    return THEMATIC_CATEGORIES.ILUMINACAO_PUBLICA;
  }
  if (norm.includes("meio ambien") || norm.includes("arvor") || norm.includes("poda") || norm.includes("animal") || norm.includes("canil") || norm.includes("castrac")) {
    return THEMATIC_CATEGORIES.MEIO_AMBIENTE;
  }
  if (norm.includes("limpez") || norm.includes("zelador") || norm.includes("lixo") || norm.includes("entulho") || norm.includes("capina") || norm.includes("rocag")) {
    return THEMATIC_CATEGORIES.LIMPEZA_ZELADORIA;
  }
  if (norm.includes("saneament") || norm.includes("agua") || norm.includes("esgoto") || norm.includes("bueiro") || norm.includes("galeria") || norm.includes("drenag")) {
    return THEMATIC_CATEGORIES.SANEAMENTO_AGUA;
  }
  if (norm.includes("esport") || norm.includes("lazer") || norm.includes("praca") || norm.includes("parque") || norm.includes("quadra") || norm.includes("campo")) {
    return THEMATIC_CATEGORIES.ESPORTE_LAZER;
  }
  if (norm.includes("saud") || norm.includes("medic") || norm.includes("hospital") || norm.includes("posto") || norm.includes("ubs") || norm.includes("vacina") || norm.includes("remedio")) {
    return THEMATIC_CATEGORIES.SAUDE;
  }
  if (norm.includes("educa") || norm.includes("escol") || norm.includes("creche") || norm.includes("emeb") || norm.includes("merenda")) {
    return THEMATIC_CATEGORIES.EDUCACAO;
  }
  if (norm.includes("seguran") || norm.includes("polic") || norm.includes("guarda") || norm.includes("gcm") || norm.includes("camera") || norm.includes("ronda")) {
    return THEMATIC_CATEGORIES.SEGURANCA;
  }
  if (norm.includes("cultur") || norm.includes("evento") || norm.includes("festa") || norm.includes("feira") || norm.includes("patrimonio") || norm.includes("carnaval")) {
    return THEMATIC_CATEGORIES.CULTURA_EVENTOS;
  }
  if (norm.includes("social") || norm.includes("habitac") || norm.includes("cras") || norm.includes("creas") || norm.includes("vulnerav") || norm.includes("cdhu") || norm.includes("moradia")) {
    return THEMATIC_CATEGORIES.ASSISTENCIA_SOCIAL_HABITACAO;
  }
  if (norm.includes("administr") || norm.includes("servidor") || norm.includes("concurso") || norm.includes("vencimento") || norm.includes("estatuto") || norm.includes("tribut") || norm.includes("iptu") || norm.includes("refis")) {
    return THEMATIC_CATEGORIES.ADMINISTRACAO;
  }

  return THEMATIC_CATEGORIES.OUTROS;
}

/**
 * Retorna o rótulo da categoria em português a partir da chave ou objeto.
 *
 * @param {string|object} categoryKeyOrName
 * @returns {string}
 */
export function getCategoryLabel(categoryKeyOrName) {
  return getCategoryStyle(categoryKeyOrName).label;
}

/**
 * Retorna os metadados amigáveis do tipo de geometria (LineString, Polygon, etc.).
 *
 * @param {string|null} geometryType
 * @returns {{ label: string, badge: string, icon: string, description: string }}
 */
export function getGeometryMetadata(geometryType) {
  if (!geometryType) return GEOMETRY_TYPE_METADATA.Point;
  return GEOMETRY_TYPE_METADATA[geometryType] || {
    label: geometryType,
    badge: geometryType,
    icon: GEOMETRY_TYPE_METADATA.Point.icon,
    description: "Geometria espacial",
  };
}

/**
 * Retorna a descrição amigável do tipo de relação espacial identificada.
 *
 * @param {string|null} relationType
 * @returns {string|null}
 */
export function getRelationLabel(relationType) {
  if (!relationType) return null;
  return RELATION_TYPE_LABELS[relationType] || relationType;
}

/**
 * Objeto unificado retrocompatível com a constante legada CATEGORY_STYLES.
 * Mapeia chaves novas em maiúsculo, rótulos formatados em português e termos legados.
 */
export const CATEGORY_STYLES = new Proxy(THEMATIC_CATEGORIES, {
  get(target, prop) {
    if (typeof prop === "string") {
      if (target[prop]) return target[prop];
      return getCategoryStyle(prop);
    }
    return target[prop];
  },
});
